import { AppNS } from 'app';
import { parseDocumentResponse } from 'app/api_v2/files/parsers';
import { FileInfo } from 'app/ui/components/multi-file-uploader2/MultiFileUploader2';
import { sortBy } from 'lodash-es';
import * as Yup from 'yup';

export interface FormValues extends Partial<AppNS.ReportAttendee> {
  name: string;
  contactEmail: string;
  contactPhone: string;
  documents: FileInfo[];
  role: string;
  organization: string;
}

export const defaultValues: FormValues = {
  contactEmail: '',
  contactPhone: '',
  name: '',
  documents: [],
  role: '',
  organization: '',
};

export const validationSchema = Yup.object({
  contactEmail: Yup.string().default('').trim().email('Invalid email address').required('Email is required'),
  contactPhone: Yup.string().default('').trim().required('Phone number is required'),
  name: Yup.string().default('').trim().required(' Name is required'),
  documents: Yup.array().default([]).required('Signature is required').min(1, 'Signature is required'),
  role: Yup.string().default('').trim().required('Role is required'),
});

export const getValidationSchema = (isSignatureNeeded: boolean) => {
  return Yup.object({
    contactEmail: Yup.string().default('').trim().email('Invalid email address').required('Email is required'),
    contactPhone: Yup.string().default('').trim().required('Phone number is required'),
    name: Yup.string().default('').trim().required(' Name is required'),
    documents: isSignatureNeeded
      ? Yup.array().default([]).required('Signature is required').min(1, 'Signature is required')
      : Yup.array().default([]).optional(),
    role: Yup.string().default('').trim().required('Role is required'),
    organization: Yup.string()
      .default('')
      .when('role', {
        is: (role: string) => {
          return role !== 'inspector';
        },
        then: () => Yup.string().default('').required('Field is required'),
      })
      .default(''),
  });
};

export const MAX_ATTENDEES = 4;

export const parseData = (data: AppNS.FileData[]): AppNS.ReportAttendee[] => {
  return data.map((item) => ({
    ...(item.metadata as AppNS.ReportAttendee),
    documents: [parseDocumentResponse(item)],
    id: item.id,
  }));
};

const getInspectorEmptyData = (report: AppNS.JobEventReport): AppNS.ReportAttendee => {
  return {
    ...defaultValues,
    id: '',
    name: report?.inspector?.fullName || '',
    contactEmail: report?.inspector?.email || '',
    contactPhone: report?.inspector?.contactPhone || '',
    role: 'inspector',
  };
};

const EMPTY_DATA = {
  ...defaultValues,
  id: '',
};

const getEmptyData = (report: AppNS.JobEventReport, collection: AppNS.ReportAttendee[]): AppNS.ReportAttendee[] => {
  if (!collection || collection.length === 0) {
    return [getInspectorEmptyData(report), EMPTY_DATA];
  }

  if (collection.length === 1 && collection[0].role === 'inspector') {
    return [
      {
        ...defaultValues,
        id: '',
      },
    ];
  }
  if (collection.length === 1 && collection[0].role !== 'inspector') {
    return [getInspectorEmptyData(report)];
  }

  return [EMPTY_DATA];
};

export const getReportAttendees = (report: AppNS.JobEventReport, isEditable: boolean): AppNS.ReportAttendee[] => {
  const attendees = report.documents?.filter((doc) => doc.type === 'report-attendee');

  if (!isEditable) {
    return parseData(attendees || []);
  }

  if (!attendees || attendees.length === 0) {
    return [
      {
        ...defaultValues,
        id: '',
        name: report?.inspector?.fullName || '',
        contactEmail: report?.inspector?.email || '',
        contactPhone: report?.inspector?.contactPhone || '',
        role: 'inspector',
      },
      {
        ...defaultValues,
        id: '',
      },
    ];
  }

  if (attendees.length > MAX_ATTENDEES) {
    return parseData(attendees);
  }

  const currentAttendees = parseData(attendees);

  return [...currentAttendees, ...getEmptyData(report, currentAttendees)];
};

export const getSortedAttendees = (attendees: AppNS.ReportAttendee[]): AppNS.ReportAttendee[] => {
  return sortBy(attendees, [
    function (o) {
      return o.role === 'inspector' ? 0 : 1;
    },
  ]);
};
